import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useExpertStripeOnboardingLink } from '~/modules/connect/useExpertStripeOnboardingLink';
import { useToast } from '~/shared/contexts/ToastContext';
import { useCurrentUser } from '~/shared/hooks/useCurrentUser';

/**
 * Stripe Connect Express payout onboarding: opens a backend-generated Account Link.
 */
const SetupPayoutsPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useCurrentUser();
  const { getOnboardingUrl, isLoading } = useExpertStripeOnboardingLink();
  const hasStripeConnectAccount = Boolean(user?.stripeConnectAccountId);

  const handleStripeOnboarding = async () => {
    try {
      const url = await getOnboardingUrl();
      if (url) {
        window.location.assign(url);
        return;
      }
      showToast(
        'Could not start Stripe onboarding. Please try again.',
        'error',
      );
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'stripe_connect_setup_payouts' },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Could not start Stripe onboarding. Please try again.';
      showToast(message, 'error');
    }
  };

  const titleText = hasStripeConnectAccount
    ? 'Finish payout setup'
    : 'Set up your payout account';

  const descriptionText = hasStripeConnectAccount
    ? 'Stripe still needs a bit more before payouts are ready. Continue on Stripe to finish verification (for example tax or identity details). You can return here anytime.'
    : "Connect a Stripe payout account to request retainers and get paid. You will complete bank and tax details on Stripe's secure site.";

  const primaryButtonLabel = hasStripeConnectAccount
    ? 'Continue in Stripe'
    : 'Set up payout account in Stripe';

  return (
    <Container>
      <Card variant="outlined">
        <Title variant="h6">{titleText}</Title>
        <Description variant="body2">{descriptionText}</Description>
        <PrimaryButton
          type="button"
          variant="contained"
          size="large"
          fullWidth
          startIcon={
            isLoading ? (
              <CircularProgress size={16} color="inherit" aria-hidden />
            ) : (
              <StyledOpenInNew />
            )
          }
          disabled={isLoading}
          aria-busy={isLoading}
          onClick={() => {
            void handleStripeOnboarding();
          }}
        >
          {isLoading ? 'Opening Stripe…' : primaryButtonLabel}
        </PrimaryButton>
        <SecondaryButton
          variant="outlined"
          fullWidth
          onClick={() => {
            navigate('/matters');
          }}
        >
          Back to matters
        </SecondaryButton>
      </Card>
    </Container>
  );
};

const Container = styled(Box)(({ theme }) => ({
  maxWidth: 480,
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const Card = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(1),
}));

const Description = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(3),
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  marginBottom: theme.spacing(2),
}));

const SecondaryButton = styled(Button)(() => ({
  textTransform: 'none',
}));

const StyledOpenInNew = styled(OpenInNew)(() => ({
  fontSize: 18,
}));

export default SetupPayoutsPage;
